import { FILE_EXTENSIONS_PATTERN } from '../../lib/file-classification'
import { isFilePathTarget, normalizeFilePathTarget } from './linkify'

const BARE_FILE_LISTING_LINE_REGEX = new RegExp(
  `^(\\s*(?:[-*+]\\s+|\\d+[.)]\\s+)?)`
  + `([^\\r\\n"'\\\`<>\\[\\]/\\\\]+\\.(?:${FILE_EXTENSIONS_PATTERN})(?:[?#].*)?)`
  + `\\s*$`,
  'i',
)

function getBareFileListingLineTarget(value: string): string | null {
  const match = value.match(BARE_FILE_LISTING_LINE_REGEX)
  return match?.[2]?.trim() || null
}

function stripSimpleListMarker(value: string): string {
  return value.replace(/^\s*(?:[-*+]\s+|\d+[.)]\s+)/, '').trim()
}

/**
 * Classify markdown link targets for click dispatch.
 * File paths are handled by onFileClick; everything else is treated as a URL.
 */
export function classifyMarkdownLinkTarget(target: string): 'file' | 'url' {
  return isFilePathTarget(target) ? 'file' : 'url'
}

/**
 * Convert file:// markdown link targets to filesystem paths before dispatching
 * to Electron's openFile path. Non-file paths are returned unchanged.
 */
export function normalizeMarkdownFileTarget(target: string): string {
  const trimmed = normalizeFilePathTarget(target)
  if (!trimmed.toLowerCase().startsWith('file://')) return trimmed

  try {
    const url = new URL(trimmed)
    let pathname = decodeURIComponent(url.pathname)

    if (/^\/[A-Za-z]:\//.test(pathname)) {
      return pathname.slice(1).replace(/\//g, '\\')
    }

    if (url.hostname) {
      return `\\\\${url.hostname}${pathname.replace(/\//g, '\\')}`
    }

    return pathname
  } catch {
    return trimmed
  }
}

export function getMarkdownFileTarget(target: string): string {
  return normalizeMarkdownFileTarget(normalizeFilePathTarget(target))
}

export function getInlineCodeFileTarget(value: string): string | null {
  const target = value.trim()
  if (!target) return null
  return classifyMarkdownLinkTarget(target) === 'file'
    ? getMarkdownFileTarget(target)
    : null
}

export function getCodeBlockLineFileTarget(
  value: string,
  options: { allowBareFileName?: boolean } = {},
): string | null {
  const target = options.allowBareFileName
    ? getBareFileListingLineTarget(value.trim()) ?? stripSimpleListMarker(value)
    : value.trim()
  if (!target) return null

  if (
    classifyMarkdownLinkTarget(target) !== 'file'
  ) {
    return null
  }

  const normalizedTarget = normalizeMarkdownFileTarget(target)
  const isAbsoluteOrExplicit = /^file:\/\//i.test(target)
    || /^[A-Za-z]:[/\\]/.test(normalizedTarget)
    || /^\\\\/.test(normalizedTarget)
    || /^(?:\/|~\/|\.\/|\.\.\/)/.test(normalizedTarget)

  if (!isAbsoluteOrExplicit && !options.allowBareFileName) return null

  return getMarkdownFileTarget(target)
}
